/**
 * BookingTokenPage ✦
 *
 * The public booking-completion surface at `/book/:token`.
 *
 * Editorial promise: this is a supplier-led booking, NOT a SaaS
 * onboarding. Calm Mode-1 atmosphere (Georgia serif, paper tones,
 * gold accents), single-column rhythm, no dashboards, no progress
 * bars, no pricing nags.
 *
 * Flow:
 *   1. /book/:token GET → reveal package + supplier + T&Cs
 *   2. Client fills the soft form (name, email, date, venue, note)
 *   3. T&C acceptance is a quiet checkbox that must settle before
 *      "Settle the booking ✦" enables
 *   4. If anonymous → soft sign-in / register pivot (split column
 *      on desktop, stacked on mobile). The booking draft is
 *      preserved in localStorage so the user returns to exactly
 *      the same state after auth.
 *   5. POST /accept → success: "The night has been added softly ✦"
 *
 * Closed states (expired / revoked / linked) get a calm message
 * with no completion affordance — the invitation has settled.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DRAFT_KEY = (token) => `eventdrop_booking_draft_${token}`;

const BookingTokenPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('form'); // form | success | auth-pivot
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  // Draft state — persisted to localStorage so the form survives
  // a sign-up round trip. Loaded once per token.
  const initialDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY(token));
      if (raw) return JSON.parse(raw);
    } catch {}
    return {
      client_name: '', client_email: '', client_phone: '',
      event_date: '', event_time: '', event_type: '',
      venue: '', venue_address: '',
      access_issues: '',
      client_note: '',
      terms_accepted: false,
      existing_event_id: '',
    };
  };
  const [draft, setDraft] = useState(initialDraft);

  // Persist on every change
  useEffect(() => {
    if (!token) return;
    try { localStorage.setItem(DRAFT_KEY(token), JSON.stringify(draft)); } catch {}
  }, [draft, token]);

  const fetchLink = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/book/${token}`)
      .then((r) => { setLink(r.data); setError(''); })
      .catch((e) => {
        setError(e?.response?.data?.detail || "This invitation couldn't be opened");
        setLink(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchLink(); }, [fetchLink]);

  // If the viewer just signed in, refresh so we pick up
  // `candidate_events` and the auth header.
  useEffect(() => {
    if (user && link && !link.viewer_signed_in) fetchLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Note: we deliberately do NOT prefill client_email from the
  // signed-in user. The booking link may be opened by someone
  // signing in on another household device — the email should be
  // entered consciously so the supplier's diary stores the right
  // contact for the real client.

  const settleBooking = async () => {
    setError('');
    if (!draft.client_name.trim()) { setError('Add your name'); return; }
    if (!draft.client_email.trim()) { setError('Add your email'); return; }
    if (!draft.client_phone.trim()) { setError('Add a phone number'); return; }
    if (!draft.event_date) { setError('Choose your event date'); return; }
    if (!draft.venue.trim()) { setError('Add the venue'); return; }
    if (!draft.terms_accepted) { setError('Accept the booking terms to continue'); return; }

    // iter234 — the booking link IS the auth (only someone with
    // the unique token can submit). We no longer force the customer
    // through a register/sign-in funnel before they can confirm
    // their event details with their supplier. The backend silently
    // resolves (or creates) a ghost user keyed on `client_email`.

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/book/${token}/accept`, {
        client_name: draft.client_name.trim(),
        client_email: draft.client_email.trim(),
        client_phone: draft.client_phone.trim(),
        event_date: draft.event_date,
        event_time: draft.event_time.trim(),
        event_type: draft.event_type.trim(),
        venue: draft.venue.trim(),
        venue_address: draft.venue_address.trim(),
        access_issues: draft.access_issues.trim(),
        client_note: draft.client_note.trim(),
        terms_accepted: true,
        existing_event_id: draft.existing_event_id || null,
      });
      setAccepted(r.data);
      setStage('success');
      try { localStorage.removeItem(DRAFT_KEY(token)); } catch {}
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        setStage('auth-pivot');
      } else {
        setError(typeof detail === 'string' ? detail : "Couldn't settle the booking — try again");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Surface>
        <p style={{ fontStyle: 'italic', color: 'rgba(244,233,210,0.5)' }}>
          Opening the invitation…
        </p>
      </Surface>
    );
  }

  if (!link) {
    return (
      <Surface>
        <Eyebrow>✦ Booking link</Eyebrow>
        <Heading>This invitation couldn't be found</Heading>
        <p style={bodyStyle}>{error || 'The link may have settled or been withdrawn.'}</p>
      </Surface>
    );
  }

  if (link.closed && link.status !== 'linked') {
    const closedCopy = {
      expired: 'This invitation has settled and is no longer open ✦',
      revoked: 'This invitation has been withdrawn by your supplier ✦',
      accepted: 'This booking has already been settled ✦',
    }[link.status] || 'This invitation is closed ✦';
    return (
      <Surface>
        <Eyebrow>✦ {link.supplier_name}</Eyebrow>
        <Heading>{link.package_name}</Heading>
        <p style={{ ...bodyStyle, marginTop: 24 }}>{closedCopy}</p>
      </Surface>
    );
  }

  if (link.status === 'linked' || stage === 'success') {
    const pdfUrl = `${API}/book/${token}/confirmation.pdf`;
    return (
      <Surface>
        <Eyebrow>✦ The night has settled</Eyebrow>
        <Heading>The night has been added softly ✦</Heading>
        <p style={{ ...bodyStyle, marginTop: 16 }}>
          Your booking with <em>{link.supplier_name}</em> is now resting quietly in their diary, and your
          EventDrop night is open for the gallery, the guestbook, and everything that will gather there.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 28 }}>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-success-download-pdf"
            style={{
              ...ghostBtn,
              textAlign: 'center',
              textDecoration: 'none',
              display: 'block',
            }}
          >
            Download confirmation ✦
          </a>
          {(accepted?.event_id || link.linked_event_id) && (
            <button
              data-testid="booking-success-open-event"
              onClick={() => navigate('/dashboard')}
              style={primaryBtn}
            >
              Open your dashboard ✦
            </button>
          )}
        </div>
        {/* Soft homepage-return sentence — quiet inline editorial only,
            never a feature grid or upsell. The booking remains the
            primary outcome. */}
        <div
          data-testid="booking-success-soft-return"
          style={{
            marginTop: 36,
            paddingTop: 22,
            borderTop: '1px solid rgba(194,157,82,0.18)',
            fontFamily: 'Georgia, serif',
            fontSize: 14,
            lineHeight: 1.65,
            color: 'rgba(244,233,210,0.62)',
            fontStyle: 'italic',
          }}
        >
          EventDrop can quietly hold the gallery, guestbook, memories, and
          return journey for the night ✦{' '}
          <a
            href="/"
            data-testid="booking-success-explore-link"
            style={{
              color: '#C29D52',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              fontStyle: 'normal',
            }}
          >
            Explore the night further ✦
          </a>
        </div>
      </Surface>
    );
  }

  if (stage === 'auth-pivot') {
    return <AuthPivot link={link} draft={draft} onBack={() => setStage('form')} />;
  }

  // Main form
  return (
    <Surface wide>
      <div style={{
        display: 'grid',
        gridTemplateColumns: user ? '1fr' : 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
        gap: 'clamp(28px, 5vw, 56px)',
        alignItems: 'start',
      }}>
        {/* Primary column — booking */}
        <div>
          <Eyebrow>✦ Booking with {link.supplier_name}</Eyebrow>
          <Heading>{link.package_name}</Heading>
          {link.supplier_note && (
            <p style={{
              ...bodyStyle, marginTop: 10, fontStyle: 'italic',
              color: 'rgba(244,233,210,0.6)',
            }}>“{link.supplier_note}”</p>
          )}

          <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FormRow label="Your name">
              <input
                data-testid="booking-form-name"
                value={draft.client_name}
                onChange={(e) => setDraft({ ...draft, client_name: e.target.value })}
                maxLength={160}
                style={fieldInput}
              />
            </FormRow>
            <FormRow label="Email">
              <input
                data-testid="booking-form-email"
                type="email"
                value={draft.client_email}
                onChange={(e) => setDraft({ ...draft, client_email: e.target.value })}
                maxLength={200}
                style={fieldInput}
              />
            </FormRow>
            <FormRow label="Phone">
              <input
                data-testid="booking-form-phone"
                type="tel"
                value={draft.client_phone}
                onChange={(e) => setDraft({ ...draft, client_phone: e.target.value })}
                maxLength={40}
                placeholder="A number to reach you on the day"
                style={fieldInput}
              />
            </FormRow>
            <FormRow label="Event date">
              <input
                data-testid="booking-form-date"
                type="date"
                value={draft.event_date}
                onChange={(e) => setDraft({ ...draft, event_date: e.target.value })}
                style={fieldInput}
              />
            </FormRow>
            <FormRow label="Time of requirement · optional" hint="When you'd like things set up — e.g. “Setup 5pm, doors 7pm, finish 11pm.”">
              <input
                data-testid="booking-form-time"
                type="text"
                value={draft.event_time}
                onChange={(e) => setDraft({ ...draft, event_time: e.target.value })}
                maxLength={120}
                placeholder="Setup 5pm · doors 7pm"
                style={fieldInput}
              />
            </FormRow>
            <FormRow label="Type of event · optional">
              <select
                data-testid="booking-form-event-type"
                value={draft.event_type}
                onChange={(e) => setDraft({ ...draft, event_type: e.target.value })}
                style={fieldInput}
              >
                <option value="">— Choose one ✦</option>
                <option value="Wedding">Wedding</option>
                <option value="Birthday">Birthday</option>
                <option value="Anniversary">Anniversary</option>
                <option value="Engagement">Engagement</option>
                <option value="Corporate">Corporate</option>
                <option value="Christmas Party">Christmas Party</option>
                <option value="Prom">Prom</option>
                <option value="Christening">Christening</option>
                <option value="Bar / Bat Mitzvah">Bar / Bat Mitzvah</option>
                <option value="Charity">Charity</option>
                <option value="Other">Other</option>
              </select>
            </FormRow>
            <FormRow label="Venue">
              <input
                data-testid="booking-form-venue"
                value={draft.venue}
                onChange={(e) => setDraft({ ...draft, venue: e.target.value })}
                maxLength={200}
                placeholder="The room where the night will live"
                style={fieldInput}
              />
            </FormRow>
            <FormRow label="Venue address · optional" hint="Street + postcode, so your supplier can find the door without asking.">
              <textarea
                data-testid="booking-form-venue-address"
                value={draft.venue_address}
                onChange={(e) => setDraft({ ...draft, venue_address: e.target.value })}
                rows={2}
                maxLength={400}
                style={{ ...fieldInput, fontFamily: 'Georgia, serif', resize: 'vertical' }}
              />
            </FormRow>
            <FormRow label="Any access issues · optional" hint="Stairs, lifts, narrow doorways, parking restrictions — anything that helps setup arrive prepared.">
              <textarea
                data-testid="booking-form-access-issues"
                value={draft.access_issues}
                onChange={(e) => setDraft({ ...draft, access_issues: e.target.value })}
                rows={2}
                maxLength={400}
                style={{ ...fieldInput, fontFamily: 'Georgia, serif', resize: 'vertical' }}
              />
            </FormRow>
            <FormRow label="A note to your supplier · optional" hint="Colour theme, dress code, surprise moments — anything the supplier should know.">
              <textarea
                data-testid="booking-form-note"
                value={draft.client_note}
                onChange={(e) => setDraft({ ...draft, client_note: e.target.value })}
                rows={3}
                maxLength={600}
                style={{ ...fieldInput, fontFamily: 'Georgia, serif', fontStyle: 'italic', resize: 'vertical' }}
              />
            </FormRow>

            {/* Option B — existing event picker */}
            {user && link.candidate_events?.length > 0 && (
              <FormRow label="Connect this to an existing night · optional" hint="Or leave blank — we'll quietly create a new EventDrop night for you.">
                <select
                  data-testid="booking-form-existing-event"
                  value={draft.existing_event_id}
                  onChange={(e) => setDraft({ ...draft, existing_event_id: e.target.value })}
                  style={fieldInput}
                >
                  <option value="">— Create a new night ✦</option>
                  {link.candidate_events.map((ev) => (
                    <option key={ev.event_id} value={ev.event_id}>
                      {ev.event_name} {ev.event_date ? `· ${ev.event_date}` : ''}
                    </option>
                  ))}
                </select>
              </FormRow>
            )}

            {/* T&C */}
            <div style={{
              marginTop: 12, padding: '14px 16px',
              background: 'rgba(7,5,4,0.6)',
              border: '1px solid rgba(244,233,210,0.1)',
              borderRadius: 3,
            }}>
              <p style={{
                fontSize: 10, fontWeight: 800, letterSpacing: '0.24em',
                textTransform: 'uppercase', color: 'rgba(244,233,210,0.55)',
                margin: 0,
              }}>✦ Booking terms</p>
              <pre
                data-testid="booking-form-terms-text"
                style={{
                  marginTop: 10, whiteSpace: 'pre-wrap',
                  fontFamily: 'Georgia, serif',
                  fontSize: 13, lineHeight: 1.55,
                  color: 'rgba(244,233,210,0.78)',
                  maxHeight: 240, overflowY: 'auto',
                }}
              >{link.terms_text}</pre>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                marginTop: 14, cursor: 'pointer',
                fontSize: 13, color: 'rgba(244,233,210,0.85)',
              }}>
                <input
                  type="checkbox"
                  data-testid="booking-form-accept"
                  checked={draft.terms_accepted}
                  onChange={(e) => setDraft({ ...draft, terms_accepted: e.target.checked })}
                  style={{ marginTop: 3, accentColor: '#C29D52' }}
                />
                <span>I understand and accept the booking terms ✦</span>
              </label>
            </div>

            {error && (
              <div style={{
                fontSize: 13, color: '#E08F8F', fontStyle: 'italic',
              }} data-testid="booking-form-error">{error}</div>
            )}

            <button
              data-testid="booking-form-submit"
              onClick={settleBooking}
              disabled={submitting}
              style={{ ...primaryBtn, marginTop: 6, opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? 'Settling…' : (user ? 'Settle the booking ✦' : 'Continue to your booking ✦')}
            </button>
          </div>
        </div>

        {/* Secondary column — soft EventDrop intro (anonymous only) */}
        {!user && (
          <aside style={{
            padding: 'clamp(24px, 4vw, 32px)',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(194,157,82,0.18)',
            borderRadius: 4,
          }}>
            <Eyebrow tone="gold">✦ A place for the night to gather</Eyebrow>
            <h3 style={{
              fontFamily: 'Georgia, serif', margin: '8px 0 14px',
              fontWeight: 500, fontSize: 'clamp(20px, 3.6vw, 24px)',
              color: '#F4E9D2',
            }}>Your night can live here too ✦</h3>
            <p style={{ ...bodyStyle, fontSize: 14 }}>
              Your booking can settle into a private EventDrop space — quietly holding the gallery,
              the guestbook, the recap film, and the return journey after the celebration.
            </p>
            <ul style={{
              listStyle: 'none', padding: 0, margin: '20px 0 0',
              display: 'flex', flexDirection: 'column', gap: 8,
              fontStyle: 'italic', fontSize: 13.5,
              color: 'rgba(244,233,210,0.62)',
            }}>
              {[
                'A private gallery for every photograph',
                'A Memory Wall for the night to gather',
                'QR uploads + a guestbook that doesn’t feel like one',
                'Booth OS compatibility if your supplier brings it',
                'A recap film that arrives quietly afterwards',
              ].map((line) => (
                <li key={line} style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: 'rgba(194,157,82,0.6)' }}>·</span>{line}
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </Surface>
  );
};

// ────────────────────────────────────────────────────
// Auth pivot — calm sign-in / register surface
// ────────────────────────────────────────────────────
const AuthPivot = ({ link, draft, onBack }) => {
  const navigate = useNavigate();
  // We send the user to /login with a `next` param that brings
  // them right back to /book/:token. The draft is already in
  // localStorage so it survives the round-trip.
  const returnTo = `/book/${window.location.pathname.split('/').pop()}`;
  return (
    <Surface>
      <Eyebrow>✦ One last quiet step</Eyebrow>
      <Heading>Continue your booking</Heading>
      <p style={{ ...bodyStyle, marginTop: 12 }}>
        Your booking with <em>{link.supplier_name}</em> needs a quiet home to settle into.
        Create your EventDrop account — or sign in — and we’ll bring you straight back to where you left off.
      </p>
      <p style={{ ...bodyStyle, marginTop: 6, fontSize: 13, color: 'rgba(244,233,210,0.5)' }}>
        Your details are held softly while you’re away — nothing has been lost.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 28 }}>
        <button
          data-testid="auth-pivot-register"
          onClick={() => navigate(`/login?mode=register&email=${encodeURIComponent(draft.client_email)}&next=${encodeURIComponent(returnTo)}`)}
          style={primaryBtn}
        >
          Create your EventDrop account ✦
        </button>
        <button
          data-testid="auth-pivot-signin"
          onClick={() => navigate(`/login?next=${encodeURIComponent(returnTo)}`)}
          style={ghostBtn}
        >
          I already have an account · Sign in
        </button>
        <button
          data-testid="auth-pivot-back"
          onClick={onBack}
          style={{
            background: 'transparent', border: 'none',
            color: 'rgba(244,233,210,0.45)', fontStyle: 'italic',
            fontSize: 13, cursor: 'pointer', marginTop: 4,
            fontFamily: 'Georgia, serif',
          }}
        >
          ← Back to the booking
        </button>
      </div>
    </Surface>
  );
};

// ────────────────────────────────────────────────────
// Atomic editorial primitives
// ────────────────────────────────────────────────────
const Surface = ({ children, wide }) => (
  <div
    data-testid="booking-token-page"
    style={{
      minHeight: '100vh',
      background: 'radial-gradient(1400px 900px at 50% -150px, #1A1208 0%, #0C0804 60%, #060403 100%)',
      color: '#F4E9D2',
      fontFamily: 'Georgia, serif',
      padding: 'clamp(40px, 7vw, 80px) clamp(20px, 5vw, 48px)',
      overflowX: 'hidden',
    }}
  >
    <div style={{
      maxWidth: wide ? 920 : 540,
      margin: '0 auto',
    }}>{children}</div>
  </div>
);

const Eyebrow = ({ children, tone }) => (
  <p style={{
    fontSize: 10, fontWeight: 800,
    letterSpacing: '0.32em', textTransform: 'uppercase',
    color: tone === 'gold' ? 'rgba(194,157,82,0.85)' : 'rgba(194,157,82,0.7)',
    margin: 0,
  }}>{children}</p>
);

const Heading = ({ children }) => (
  <h1 style={{
    fontFamily: 'Georgia, serif',
    fontSize: 'clamp(26px, 5.6vw, 40px)',
    fontWeight: 500, letterSpacing: '-0.015em',
    lineHeight: 1.18, margin: '10px 0 0',
    textShadow: '0 0 22px rgba(194,157,82,0.18)',
  }}>{children}</h1>
);

const bodyStyle = {
  margin: 0,
  fontSize: 'clamp(14px, 3.4vw, 15px)',
  lineHeight: 1.65,
  color: 'rgba(244,233,210,0.75)',
};

const FormRow = ({ label, hint, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: 10, fontWeight: 800,
      letterSpacing: '0.24em', textTransform: 'uppercase',
      color: 'rgba(244,233,210,0.55)', marginBottom: 6,
    }}>{label}</label>
    {children}
    {hint && (
      <p style={{
        margin: '6px 0 0', fontStyle: 'italic',
        fontSize: 11.5, color: 'rgba(244,233,210,0.42)',
      }}>{hint}</p>
    )}
  </div>
);

const fieldInput = {
  width: '100%',
  background: '#070504',
  border: '1px solid rgba(244,233,210,0.14)',
  color: '#F4E9D2',
  padding: '11px 13px',
  borderRadius: 3,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'Inter, system-ui, sans-serif',
};

const primaryBtn = {
  width: '100%',
  background: '#C29D52',
  color: '#100A04',
  border: '1px solid #C29D52',
  padding: '13px 18px',
  borderRadius: 3,
  fontFamily: 'Georgia, serif',
  fontStyle: 'italic',
  fontSize: 15,
  cursor: 'pointer',
};

const ghostBtn = {
  width: '100%',
  background: 'transparent',
  color: 'rgba(244,233,210,0.75)',
  border: '1px solid rgba(244,233,210,0.18)',
  padding: '13px 18px',
  borderRadius: 3,
  fontFamily: 'Georgia, serif',
  fontStyle: 'italic',
  fontSize: 15,
  cursor: 'pointer',
};

export default BookingTokenPage;
